import { Box, Button, Flex } from '@chakra-ui/react';
import { PermissionBadge, hasResourcePermission } from 'modules/User';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MapHeader } from './MapHeader';
import {
  ALL_MAP_CONFIGS,
  ALL_US_STATES_TITLES,
  CANADA_STATES_TITLES,
  CONTINENT_TITLES,
  COUNTRY_TITLES,
  DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
  FREE_USER_MAPS,
  MAP_DISPLAY_NAMES,
  OTHER_TITLES,
} from './MapWidgetMenu.config';
import { AllMapListProps, MapCreationType, MapListButtonProps } from './MapWidgetMenu.types';

const MapListButton = ({ title, onClick, isAccessAllowed }: MapListButtonProps) => {
  const mapDisplayTitle = MAP_DISPLAY_NAMES[title as keyof typeof MAP_DISPLAY_NAMES] || (title as string);
  const isFreeMap = FREE_USER_MAPS[mapDisplayTitle as keyof typeof FREE_USER_MAPS]!!;
  const onMapListButtonClick = () => onClick(mapDisplayTitle);

  return (
    <Button
      role='group'
      onClick={onMapListButtonClick}
      justifyContent='flex-start'
      fontWeight='normal'
      variant='ghost-with-active'
      overflow='hidden'
      whiteSpace='nowrap'
    >
      {!isAccessAllowed && !isFreeMap && (
        <PermissionBadge permission={DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION} position='absolute' right='2' />
      )}
      <Box as='span' textOverflow='ellipsis' overflow='hidden' maxWidth='87%'>
        {title}
      </Box>
    </Button>
  );
};

const ListContainer = ({ children }: any) => {
  return (
    <Flex direction='column' gap={1}>
      {children}
    </Flex>
  );
};

export const AllMapList = memo(({ onClickUpgrade, handleAddWidget, userType }: AllMapListProps) => {
  const isAccessAllowed = hasResourcePermission(userType, DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION);
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.mapsMenu' });

  // Render the rest of the maps after the initial render
  // to keep the initial render fast so that the maps widget menu can be opened without delay
  const [shouldDisplayAllMaps, setDisplayAllMaps] = useState(false);

  const onAddMapWidget = async (label: string) => {
    const isFreeMap = FREE_USER_MAPS[label as keyof typeof FREE_USER_MAPS]!!;
    if (!isAccessAllowed && !isFreeMap) return onClickUpgrade();

    const config = ALL_MAP_CONFIGS.find((c) => c.label === label);

    // should not get here but just in case
    if (!config) {
      console.error('Could not find map config for label', label);
      return;
    }

    handleAddWidget(config, MapCreationType.List);
  };

  // Display all maps after the initial render so that the maps widget menu is still opened without delay
  useEffect(() => setDisplayAllMaps(true), []);

  return (
    <Flex direction='column' gap={4}>
      <MapHeader label={t('continentsHeader')} />
      <ListContainer>
        {CONTINENT_TITLES.map((title) => (
          <MapListButton
            key={title}
            title={title}
            onClick={() => onAddMapWidget(title)}
            isAccessAllowed={isAccessAllowed}
          />
        ))}
      </ListContainer>
      {shouldDisplayAllMaps && (
        <>
          <MapHeader label={t('countriesHeader')} />
          <ListContainer>
            {COUNTRY_TITLES.map((title) => (
              <MapListButton key={title} title={title} onClick={onAddMapWidget} isAccessAllowed={isAccessAllowed} />
            ))}
          </ListContainer>

          <MapHeader label={t('mapButtonLabels.unitedStates')} />
          <ListContainer>
            {ALL_US_STATES_TITLES.map((title) => (
              <MapListButton key={title} title={title} onClick={onAddMapWidget} isAccessAllowed={isAccessAllowed} />
            ))}
          </ListContainer>
          <MapHeader label={t('mapButtonLabels.canada')} />
          <ListContainer>
            {CANADA_STATES_TITLES.map((title) => (
              <MapListButton key={title} title={title} onClick={onAddMapWidget} isAccessAllowed={isAccessAllowed} />
            ))}
          </ListContainer>
          <MapHeader label={t('otherHeader')} />
          <ListContainer>
            {OTHER_TITLES.map((title) => (
              <MapListButton key={title} title={title} onClick={onAddMapWidget} isAccessAllowed={isAccessAllowed} />
            ))}
          </ListContainer>
        </>
      )}
    </Flex>
  );
});
AllMapList.displayName = 'AllMapList';
